import { useState, useEffect, useCallback } from 'react';
import {
  fetchDriverMissions,
  fetchAvailableDriverMissions,
  acceptDriverMission,
  rejectDriverMission,
  completeDriverMission,
  type DriverMission,
} from '@/domains/driver/services/driver.service';

export type MissionStatusFilter = 'all' | 'available' | 'pending' | 'in_progress' | 'delivered' | 'rejected' | 'cancelled';

export function useDriverMissions() {
  const [missions, setMissions] = useState<DriverMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<MissionStatusFilter>('all');

  const loadMissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeFilter === 'all') {
        const [assigned, available] = await Promise.all([
          fetchDriverMissions(undefined),
          fetchAvailableDriverMissions(),
        ]);
        setMissions([...available, ...assigned.filter((m) => !available.some((a) => a.id === m.id))]);
      } else {
        const data = await fetchDriverMissions(activeFilter);
        setMissions(data);
      }
    } catch {
      setError('Impossible de charger vos missions.');
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  const filteredMissions = missions;

  const pendingMissions = missions.filter((m) => m.status === 'pending');
  const activeMissions = missions.filter((m) => m.status === 'in_progress');

  const handleAccept = async (missionId: number, isAvailable?: boolean) => {
    setActionLoading(true);
    try {
      await acceptDriverMission(missionId, isAvailable);
      await loadMissions();
    } catch {
      setError('Échec de l\'acceptation de la mission.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (missionId: number, isAvailable?: boolean) => {
    setActionLoading(true);
    try {
      await rejectDriverMission(missionId, isAvailable);
      await loadMissions();
    } catch {
      setError('Échec du refus de la mission.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (missionId: number) => {
    setActionLoading(true);
    try {
      await completeDriverMission(missionId);
      await loadMissions();
    } catch {
      setError('Échec de la confirmation de livraison.');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    missions: filteredMissions,
    pendingMissions,
    activeMissions,
    loading,
    actionLoading,
    error,
    activeFilter,
    setActiveFilter,
    reload: loadMissions,
    handleAccept,
    handleReject,
    handleComplete,
  };
}
